import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import AdminAuth from '../components/admin/AdminAuth';
import AdminDashboard from '../components/admin/AdminDashboard';
import AdminUsers from '../components/admin/AdminUsers';
import AdminTransactions from '../components/admin/AdminTransactions';
import AdminItems from '../components/admin/AdminItems';
import AdminCharities from '../components/admin/AdminCharities';
import AdminCharityApplications from '../components/admin/AdminCharityApplications';
import AdminCharityPayouts from '../components/admin/AdminCharityPayouts';
import AdminRetailerPayouts from '../components/admin/AdminRetailerPayouts';
import AdminRetailers from '../components/admin/AdminRetailers';
import AdminApprovals from '../components/admin/AdminApprovals';
import AdminAnalytics from '../components/admin/AdminAnalytics';
import AdminCuration from '../components/admin/AdminCuration';
import AdminIncidents from '../components/admin/AdminIncidents';
import AdminTelemetryDashboard from '../components/admin/AdminTelemetryDashboard';
import AdminPilotHealth from '../components/admin/AdminPilotHealth';
import AdminTaxReports from '../components/admin/AdminTaxReports';
import AdminOps from '../components/admin/AdminOps';
import AdminST1Preview from '../components/admin/AdminST1Preview';
import AdminST1Filings from '../components/admin/AdminST1Filings';
import AdminST1Automation from '../components/admin/AdminST1Automation';
import AdminSmsOptins from '../components/admin/AdminSmsOptins';
import AdminRefundAdjustments from '../components/admin/AdminRefundAdjustments';
import AdminDisputeReview from '../components/admin/AdminDisputeReview';
import AdminReconciliation from '../components/admin/AdminReconciliation';
import AdminDrlpApi from '../components/admin/AdminDrlpApi';
import AdminDrlpOnboarding from '../components/admin/AdminDrlpOnboarding';
import AdminSystemHealth from '../components/admin/AdminSystemHealth';
import AdminImpactDashboard from '../components/admin/AdminImpactDashboard';
import AdminSeedCleanup from '../components/admin/AdminSeedCleanup';
import AdminTaxExceptions from '../components/admin/AdminTaxExceptions';
import AdminLayout from '../components/admin/AdminLayout';
// Help Center
import HelpPage from '../pages/HelpPage';
import { FloatingHelpButton } from '../components/shared/FloatingHelpButton';
import BuildInfo from '../components/BuildInfo';
import Footer from '../components/legal/Footer';

export default function AdminApp() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      const parsedUser = JSON.parse(savedUser);
      if (parsedUser.role === 'Admin' || parsedUser.role === 'ADMIN') {
        setUser(parsedUser);
      } else {
        localStorage.clear();
      }
    }
    setLoading(false);
  }, []);

  const handleLogin = (responseData) => {
    const { access_token, user: userData } = responseData;
    if (userData.role !== 'ADMIN' && userData.role !== 'Admin') {
      throw new Error('Invalid user role for Admin App');
    }
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    // Notify same-tab listeners (LegalAcceptanceGate) that auth changed.
    window.dispatchEvent(new Event('auth-changed'));
    setUser(userData);
    navigate('/admin/dashboard');
  };

  const handleLogout = () => {
    localStorage.clear();
    window.dispatchEvent(new Event('auth-changed'));
    setUser(null);
    navigate('/admin');
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  // Allow unauthenticated access to help page
  if (location.pathname === '/admin/help') {
    return (
      <>
        <Routes>
          <Route path="/help" element={<HelpPage />} />
        </Routes>
        <FloatingHelpButton userRole="admin" />
        <BuildInfo />
      </>
    );
  }

  if (!user) {
    return (
      <>
        <AdminAuth onLogin={handleLogin} />
        <BuildInfo />
      </>
    );
  }

  return (
    <>
      <Routes>
        <Route path="/" element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="/dashboard" element={<AdminDashboard user={user} onLogout={handleLogout} />} />
        <Route path="/pilot-health" element={<AdminPilotHealth user={user} onLogout={handleLogout} />} />
        <Route path="/analytics" element={
          <AdminLayout user={user} onLogout={handleLogout}>
            <AdminAnalytics />
          </AdminLayout>
        } />
        <Route path="/curation" element={
          <AdminLayout user={user} onLogout={handleLogout}>
            <AdminCuration />
          </AdminLayout>
        } />
        <Route path="/approvals" element={<AdminApprovals user={user} onLogout={handleLogout} />} />
        <Route path="/users" element={<AdminUsers user={user} onLogout={handleLogout} />} />
        <Route path="/retailers" element={<AdminRetailers user={user} onLogout={handleLogout} />} />
        <Route path="/seed-cleanup" element={<AdminSeedCleanup user={user} onLogout={handleLogout} />} />
        <Route path="/retailer-payouts" element={<AdminRetailerPayouts user={user} onLogout={handleLogout} />} />
        <Route path="/transactions" element={<AdminTransactions user={user} onLogout={handleLogout} />} />
        <Route path="/items" element={<AdminItems user={user} onLogout={handleLogout} />} />
        <Route path="/charities" element={<AdminCharities user={user} onLogout={handleLogout} />} />
        <Route path="/charity-applications" element={<AdminCharityApplications user={user} onLogout={handleLogout} />} />
        <Route path="/charity-payouts" element={<AdminCharityPayouts user={user} onLogout={handleLogout} />} />
        <Route path="/incidents" element={<AdminIncidents user={user} onLogout={handleLogout} />} />
        <Route path="/tax-reports" element={<AdminTaxReports user={user} onLogout={handleLogout} />} />
        <Route path="/st1-preview" element={<AdminST1Preview user={user} onLogout={handleLogout} />} />
        <Route path="/st1-filings" element={<AdminST1Filings user={user} onLogout={handleLogout} />} />
        <Route path="/sms-optins" element={<AdminSmsOptins user={user} onLogout={handleLogout} />} />
        <Route path="/refunds" element={<AdminRefundAdjustments user={user} onLogout={handleLogout} />} />
        <Route path="/disputes" element={<AdminDisputeReview user={user} onLogout={handleLogout} />} />
        <Route path="/reconciliation" element={<AdminReconciliation user={user} onLogout={handleLogout} />} />
        <Route path="/tax-exceptions" element={<AdminTaxExceptions user={user} onLogout={handleLogout} />} />
        <Route path="/drlp-api" element={<AdminDrlpApi user={user} onLogout={handleLogout} />} />
        <Route path="/drlp-onboarding" element={<AdminDrlpOnboarding user={user} onLogout={handleLogout} />} />
        <Route path="/st1-automation" element={<AdminST1Automation user={user} onLogout={handleLogout} />} />
        <Route path="/system-health" element={<AdminSystemHealth user={user} onLogout={handleLogout} />} />
        <Route path="/impact" element={
          <AdminLayout user={user} onLogout={handleLogout}>
            <AdminImpactDashboard />
          </AdminLayout>
        } />
        <Route path="/ops" element={<AdminOps user={user} onLogout={handleLogout} />} />
        <Route path="/telemetry" element={
          <AdminLayout user={user} onLogout={handleLogout}>
            <AdminTelemetryDashboard />
          </AdminLayout>
        } />
        <Route path="/help" element={<HelpPage />} />
      </Routes>
      <FloatingHelpButton userRole="admin" />
      <Footer variant="admin" />
      <BuildInfo />
    </>
  );
}
